import { createHelpArticle } from '../helpModel'

export const messagingArticles = [
  createHelpArticle({
    id: 'message-send', slug: 'send-a-message', title: 'Send a message', category: 'Messaging & Chat',
    description: 'Open Messages from the app’s Messages shortcut or a profile’s message action, then start or continue a conversation.',
    keywords: ['message', 'chat', 'conversation', 'send'],
    aliases: ['how to chat', 'new message', 'text someone', 'start a private conversation'],
    steps: [
      { title: 'Open Messages', body: 'Choose the Messages shortcut in the app header, or use the message action on a person’s profile.' },
      { title: 'Choose a conversation', body: 'Open an existing conversation or choose the new-message action to find someone.' },
      { title: 'Write and send', body: 'Type in the message field and choose Send.' },
    ],
    notes: ['Messages is not a bottom-navigation tab. Message availability depends on the other person’s privacy settings, blocks, and any account restrictions.'],
    relatedArticles: ['message-send-error', 'message-requests', 'message-media', 'block'],
    searchPriority: 4,
  }),
  createHelpArticle({
    id: 'message-send-error', slug: 'message-not-sending', title: 'Message not sending', category: 'Messaging & Chat',
    description: 'Check connectivity, retry a failed message, and consider whether the conversation is currently available.',
    keywords: ['message', 'sending', 'failed', 'chat', 'retry'],
    aliases: ['messages are not sending', 'chat failed', 'cannot send message', 'text stuck sending'],
    steps: [
      { title: 'Check your connection', body: 'Messages need an active internet connection to send.' },
      { title: 'Retry a failed message', body: 'If the message shows a retry option, use it once the connection is available.' },
      { title: 'Check conversation access', body: 'A block, the recipient’s message privacy settings, or a platform restriction can affect whether you can message.' },
      { title: 'Reopen the conversation', body: 'Return to Messages and open the conversation again if it is not loading.' },
    ],
    notes: ['A personal profile “Restrict” action is not the same thing as a platform messaging restriction; no specific personal-restrict behavior is promised.'],
    relatedArticles: ['message-send', 'message-requests', 'message-restriction'],
  }),
  createHelpArticle({
    id: 'message-reply', slug: 'reply-to-message', title: 'Reply to a message', category: 'Messaging & Chat',
    description: 'Use a message’s Reply action to quote it in your conversation.',
    keywords: ['reply', 'message', 'quote', 'conversation'],
    aliases: ['respond to a message', 'reply in chat', 'quote message'],
    steps: [
      { title: 'Open the conversation', body: 'Open Messages and choose the conversation.' },
      { title: 'Choose Reply', body: 'Open the message actions and select “Reply”.' },
      { title: 'Write and send', body: 'Write your response in the composer and choose Send.' },
      { title: 'Open the reference', body: 'If a message includes a quoted reference, select it to navigate to the referenced message or shared post when available.' },
    ],
    relatedArticles: ['message-send', 'message-send-error'],
  }),
  createHelpArticle({
    id: 'message-requests', slug: 'accept-or-remove-a-message-request', title: 'Accept, remove, or block a message request', category: 'Messaging & Chat',
    description: 'Preview messages from people outside your inbox before choosing whether to accept, remove, or block the request.',
    keywords: ['message requests', 'accept', 'remove', 'block', 'inbox'],
    aliases: ['unknown person messaged me', 'message request inbox', 'decline chat request'],
    steps: [
      { title: 'Open Messages', body: 'Open Messages using the app’s Messages shortcut.' },
      { title: 'Open Requests', body: 'Choose “Requests” to see messages from people outside your inbox.' },
      { title: 'Review the request', body: 'Open a request to preview its message and any supported attachment.' },
      { title: 'Choose an action', body: 'Select “Accept”, “Remove”, or “Block” based on what you want to do.' },
    ],
    notes: ['Accepting a request allows the person to message you again and see your read status, as stated on the request screen.'],
    relatedArticles: ['message-send', 'block', 'message-report'],
  }),
  createHelpArticle({
    id: 'message-media', slug: 'send-photos-videos-and-voice-notes', title: 'Send photos, videos, or a voice note', category: 'Messaging & Chat',
    description: 'Chat supports photo/video selection, emoji/GIF/sticker tools, and recording a voice note.',
    keywords: ['chat media', 'photos', 'videos', 'voice note', 'gif', 'sticker'],
    aliases: ['send picture in chat', 'attach a video', 'record audio message', 'send a voice message'],
    steps: [
      { title: 'Open a conversation', body: 'Choose the conversation in Messages.' },
      { title: 'Choose a media action', body: 'Use the gallery control to select photos or videos, or open the emoji/sticker/GIF picker.' },
      { title: 'Record a voice note', body: 'When the message field is empty, use the microphone control and allow microphone access if requested. Stop the recording to send it, or cancel it.' },
    ],
    notes: ['Voice notes are recorded audio messages, not voice or video calls. The gallery control does not provide a general audio-file upload.'],
    troubleshooting: [{ problem: 'The media picker is empty.', solution: 'Allow photo/video access in the device or browser permission prompt, or use “Choose photos or videos” when available.' }],
    relatedArticles: ['message-send', 'message-send-error', 'microphone-permission'],
  }),
  createHelpArticle({
    id: 'message-edit', slug: 'edit-a-chat-message', title: 'Edit a chat message', category: 'Messaging & Chat',
    description: 'A sender can edit their own text message from its available message actions.',
    keywords: ['edit message', 'change chat text', 'fix typo'],
    aliases: ['correct a message', 'change something I sent', 'chat message edit'],
    steps: [
      { title: 'Open your sent text message', body: 'In the conversation, open the actions for a text message you sent.' },
      { title: 'Choose Edit message', body: 'Select “Edit message”, update the text, then save using the action shown.' },
    ],
    notes: ['The edit action is only shown for your own text messages; it is not a general edit control for attachments.'],
    relatedArticles: ['message-reply', 'message-delete'],
  }),
  createHelpArticle({
    id: 'message-delete', slug: 'delete-a-chat-message', title: 'Delete a chat message', category: 'Messaging & Chat',
    description: 'Message actions include “Delete for me” and “Delete for everyone”, but recipient-preservation behavior is not guaranteed.',
    keywords: ['delete message', 'remove chat', 'delete for me', 'delete for everyone'],
    aliases: ['remove a message I sent', 'erase message', 'delete chat text'],
    steps: [
      { title: 'Open message actions', body: 'Open the actions for your own message.' },
      { title: 'Review the available options', body: 'The menu may show “Delete for me” and “Delete for everyone”; review the confirmation before choosing.' },
      { title: 'Confirm only if appropriate', body: 'Use “Delete for everyone” only when you intend to remove the message for everyone and the confirmation describes that result.' },
    ],
    notes: ['Do not assume “Delete for me” preserves the message for the recipient. Current server behavior does not establish viewer-only deletion. Do not use it as a confidential-data-removal guarantee.'],
    relatedArticles: ['message-report', 'message-send', 'privacy'],
  }),
  createHelpArticle({
    id: 'message-report', slug: 'report-a-chat-message', title: 'Report a threatening or abusive message', category: 'Messaging & Chat',
    description: 'The actions for another person’s chat message include a Report message control.',
    keywords: ['report message', 'threatening chat', 'abusive message', 'safety'],
    aliases: ['report someone’s dm', 'flag a chat message', 'report harassment in messages'],
    steps: [
      { title: 'Open the conversation', body: 'Open the conversation containing the message.' },
      { title: 'Open message actions', body: 'Choose the actions for the other person’s message and select “Report message”.' },
      { title: 'Submit the report', body: 'Enter the requested reason/details in the report dialog and submit it.' },
    ],
    notes: ['The report dialog states the reported message and attachment will be reviewed. This does not mean all private chats are routinely read, and no response time or outcome is guaranteed.'],
    relatedArticles: ['block', 'safety-threats', 'reporting-outcomes'],
  }),
  createHelpArticle({
    id: 'message-block', slug: 'block-or-unblock-from-chat', title: 'Block or unblock someone from chat', category: 'Messaging & Chat',
    description: 'Block a person from a message request or profile, and manage blocked users in Privacy settings.',
    keywords: ['block chat', 'unblock', 'messages', 'blocked users'],
    aliases: ['stop someone messaging me', 'block a dm', 'unblock person in chat'],
    steps: [
      { title: 'Block from a request or profile', body: 'Use the “Block” action on a message request or the available block action on a profile.' },
      { title: 'Review blocked users', body: 'Open Settings → Privacy → Blocked users.' },
      { title: 'Unblock if needed', body: 'Choose the available unblock action beside that person.' },
    ],
    notes: ['Blocking stops new contact through the supported block controls; it does not erase the existing conversation history.'],
    relatedArticles: ['block', 'message-requests', 'privacy'],
  }),
  createHelpArticle({
    id: 'message-restriction', slug: 'messaging-restrictions', title: 'Why can’t I send a message?', category: 'Messaging & Chat',
    description: 'A message may be unavailable because of recipient privacy, blocking, account status, or a platform restriction.',
    keywords: ['messaging restriction', 'read only', 'cannot message', 'blocked'],
    aliases: ['messaging disabled', 'account cannot send messages', 'chat restriction'],
    body: ['Check the message shown in the conversation and the recipient’s available privacy options. A platform-enforced restriction is different from the personal profile “Restrict” action.'],
    notes: ['The behavior of the personal Restrict state is not verified; do not rely on it as a messaging or safety control.'],
    relatedArticles: ['message-send-error', 'privacy', 'profile-restrict-limit'],
  }),
  createHelpArticle({
    id: 'group-create', slug: 'create-a-group-chat', title: 'Create a group chat', category: 'Messaging & Chat',
    description: 'Select people, choose a group name, and create a private group conversation.',
    keywords: ['group chat', 'create group', 'invite members', 'private group'],
    aliases: ['start a group conversation', 'make a chat group', 'add people to group'],
    steps: [
      { title: 'Open New Message', body: 'Open Messages and choose the new-message action.' },
      { title: 'Choose Create group', body: 'Select “Create group” and choose the people to add.' },
      { title: 'Add group details', body: 'Enter a group name and optionally choose a group photo.' },
      { title: 'Create the conversation', body: 'Choose “Create Group”.' },
    ],
    relatedArticles: ['group-manage', 'message-media', 'notification-settings'],
  }),
  createHelpArticle({
    id: 'group-manage', slug: 'leave-or-manage-a-group-chat', title: 'Leave or manage a group chat', category: 'Messaging & Chat',
    description: 'Group information offers member, media, and management controls according to your group role.',
    keywords: ['leave group', 'group settings', 'group roles', 'remove member'],
    aliases: ['exit group chat', 'change group name', 'group owner', 'group admin'],
    steps: [
      { title: 'Open group information', body: 'Open the group conversation and choose its title or Group info.' },
      { title: 'Review available tabs and actions', body: 'Use the tabs shown for members, media, files, or links.' },
      { title: 'Use a role-eligible action', body: 'The group owner/admin controls vary by your role. If leaving or deleting is shown, read its confirmation carefully.' },
    ],
    notes: ['Do not assume that every member can edit group details, manage roles, or delete a group.'],
    relatedArticles: ['group-create', 'message-send', 'group-restrictions'],
  }),
  createHelpArticle({
    id: 'group-restrictions', slug: 'group-roles-and-permissions', title: 'Group roles and permissions', category: 'Messaging & Chat',
    description: 'Group owners, admins, moderators, and members can have different controls in Group info.',
    keywords: ['group roles', 'permissions', 'owner', 'admin', 'moderator'],
    aliases: ['who can manage my group', 'group admin controls', 'promote group member'],
    body: ['Open Group info to see the controls available to your role. Some membership, role, and group-setting actions are restricted to eligible owners or admins.'],
    notes: ['Controls are permission-dependent; do not infer access from another group or account.'],
    relatedArticles: ['group-manage', 'group-create'],
  }),
  createHelpArticle({
    id: 'calls-unavailable', slug: 'voice-or-video-calls', title: 'Voice or video calls', category: 'Messaging & Chat',
    description: 'A reachable user-facing voice/video call workflow was not verified in the current app.',
    keywords: ['call', 'voice call', 'video call', 'chat'],
    aliases: ['call someone in Campnect', 'start a video call', 'phone call'],
    body: ['Chat currently supports text, selected media, and recorded voice notes. Do not rely on an unused call screen or the “Call me” privacy setting as evidence that calling is available.'],
    published: false,
    relatedArticles: ['message-media', 'privacy'],
  }),
]